// src/router/index.js
import { createRouter, createWebHistory } from 'vue-router'
import store from './store'

const routes = [
  {
    path: '/',
    name: 'LoginPage',
    component: () => import('@/views/LoginPage.vue'),
  },
  {
    path: '/email_val',
    name: 'ValidateEmailPage',
    component: () => import('@/views/ValidateEmailPage.vue'),
  },
  {
    path: '/pass_reset',
    name: 'PasswordResetPage',
    component: () => import('@/views/PasswordResetPage.vue'),
  },
  {
    path: '/loggers',
    name: 'LoggerSelectPage',
    component: () => import('@/views/LoggerSelectPage.vue'),
    meta: {
      auth: 'USER',
      navbar: true,
      breadcrumbs: [{ text: 'Loggers', to: '/loggers' }],
    },
  },
  {
    path: '/logger/:lid',
    name: 'LoggerPage',
    component: () => import('@/views/LoggerPage.vue'),
    meta: {
      auth: 'USER',
      navbar: true,
      breadcrumbs: [
        { text: 'Loggers', to: '/loggers' },
        { text: 'Loading...', to: null },
      ],
    },
  },
  {
    path: '/feedback',
    name: 'FeedbackPage',
    component: () => import('@/views/FeedbackPage.vue'),
    meta: {
      auth: 'USER',
      navbar: true,
      breadcrumbs: [
        { text: 'Loggers', to: '/loggers' },
        { text: 'Feedback', to: '/feedback' },
      ],
    },
  },
  {
    path: '/help',
    name: 'HelpPage',
    component: () => import('@/views/HelpPage.vue'),
    meta: {
      auth: 'USER',
      navbar: true,
      breadcrumbs: [
        { text: 'Loggers', to: '/loggers' },
        { text: 'Help', to: '/help' },
      ],
    },
  },
  {
    path: '/profile',
    name: 'ProfilePage',
    component: () => import('@/views/ProfilePage.vue'),
    meta: {
      auth: 'USER',
      navbar: true,
      breadcrumbs: [
        { text: 'Loggers', to: '/loggers' },
        { text: 'Profile', to: '/profile' },
      ],
    },
  },
  {
    path: '/oem',
    name: 'OEMPage',
    component: () => import('@/views/OEMPage.vue'),
    meta: {
      auth: 'OEM',
      navbar: true,
      breadcrumbs: [
        { text: 'Loggers', to: '/loggers' },
        { text: 'Admin', to: '/oem' },
      ],
    },
  },
  {
    path: '/oem/user',
    name: 'OEMUserPage',
    component: () => import('@/views/OEMUserPage.vue'),
    meta: {
      auth: 'OEM',
      navbar: true,
      breadcrumbs: [
        { text: 'Loggers', to: '/loggers' },
        { text: 'Admin', to: '/oem' },
        { text: 'User Settings', to: '/oem/user' },
      ],
    },
  },
  {
    path: '/oem/user/:uid',
    name: 'OEMUserEditPage',
    component: () => import('@/views/OEMUserEditPage.vue'),
    meta: {
      auth: 'OEM',
      navbar: true,
      breadcrumbs: [
        { text: 'Loggers', to: '/loggers' },
        { text: 'Admin', to: '/oem' },
        { text: 'User Settings', to: '/oem/user' },
        { text: 'Loading...', to: '#' },
      ],
    },
  },
  {
    path: '/oem/logger',
    name: 'OEMLoggerPage',
    component: () => import('@/views/OEMLoggerPage.vue'),
    meta: {
      auth: 'OEM',
      navbar: true,
      breadcrumbs: [
        { text: 'Loggers', to: '/loggers' },
        { text: 'Admin', to: '/oem' },
        { text: 'Logger Settings', to: '/oem/logger' },
      ],
    },
  },
  {
    path: '/oem/logger/:lid',
    name: 'OEMLoggerEditPage',
    component: () => import('@/views/OEMLoggerEditPage.vue'),
    meta: {
      auth: 'OEM',
      navbar: true,
      breadcrumbs: [
        { text: 'Loggers', to: '/loggers' },
        { text: 'Admin', to: '/oem' },
        { text: 'Logger Settings', to: '/oem/logger' },
        { text: 'Loading...', to: '#' },
      ],
    },
  },
  {
    path: '/oem/system',
    name: 'OEMSystemPage',
    component: () => import('@/views/OEMSystemPage.vue'),
    meta: {
      auth: 'OEM',
      navbar: true,
      breadcrumbs: [
        { text: 'Loggers', to: '/loggers' },
        { text: 'Admin', to: '/oem' },
        { text: 'System Settings', to: '/oem/system' },
      ],
    },
  },
  // Vue Router 4 wildcard for NotFound
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFoundPage',
    component: () => import('@/views/NotFoundPage.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  linkExactActiveClass: 'active',
})

/**
 * Navigation Guard for Authenticated Pages
 */
router.beforeEach(async (to, from, next) => {
  if (store.getters['user/auth'] === 'reauth') {
    try {
      await store.dispatch('user/reauth')
    } catch (e) {
      console.error('Session restoration failed:', e)
      
      // If session restoration fails, wipe local cache state immediately
      localStorage.removeItem('user.id')
      return next({
        name: 'LoginPage',
        replace: true,
        query: { session_expired: 'true' }
      })
    }
  }

  const authState = store.getters['user/auth']
  const userAccessLevel = store.getters['user/accessLevel']

  if (to.meta.auth && authState === 'unauthenticated') {
    return next({
      name: 'LoginPage',
      replace: true,
      query: {
        not_logged_in: String(!localStorage.getItem('user.id')),
        session_expired: String(!!localStorage.getItem('user.id')),
        from: to.path,
      },
    })
  }

  if (to.meta.auth === 'OEM' && userAccessLevel !== 'OEM') {
    return next({ 
      name: 'LoggerSelectPage', 
      replace: true,
      query: { unauthorised: 'true' }
    })
  }

  if (to.name === 'LoginPage' && authState === 'authenticated') {
    return next({
      name: 'LoggerSelectPage',
      replace: true,
    })
  }

  return next()
})

export default router
